// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { useShellStore } from '../../stores/shellStore';
import Button from './Button';
import './BackendUnreachableBanner.css';

/**
 * Shown when a request couldn't reach the backend at all (connection
 * refused / DNS / offline) — set by `apiRequest`'s network-error branch.
 * Distinct from SessionExpiredModal. The wording is a
 * neutral outage notice: in production this is an outage an operator should
 * escalate, not a "start your server" tip. The dev hint only appears when the
 * app itself is served from localhost.
 */
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]'];
const isLocalDev = () =>
  typeof window !== 'undefined' && LOCAL_HOSTS.includes(window.location.hostname);

function BackendUnreachableBanner() {
  const backendUnreachable = useShellStore(s => s.backendUnreachable);
  const setBackendUnreachable = useShellStore(s => s.setBackendUnreachable);

  if (!backendUnreachable) return null;

  return (
    <div className="backend-unreachable-banner" role="alert">
      <span className="backend-unreachable-banner-text">
        Helios can&apos;t reach its backend right now. Data on screen may be out of date; it will
        reconnect on the next successful request.
        {isLocalDev() && ' Running locally? The BFF server is probably not started.'}
      </span>
      <div className="backend-unreachable-banner-actions">
        <Button variant="ghost" size="small" onClick={() => window.location.reload()}>
          Reload
        </Button>
        <Button variant="ghost" size="small" onClick={() => setBackendUnreachable(false)}>
          Dismiss
        </Button>
      </div>
    </div>
  );
}

export default BackendUnreachableBanner;
